import wordCount from 'word-count';

import { BuilderView } from '../types';
import isEqual from 'lodash/fp/isEqual';
import { stripHTML } from 'src/hooks/useStrippedHTML';
import {
  AutoflowVersionHistorySummary,
  CustomEventCustomizationSettings,
} from 'src/types/workflowBuilderAPITypes';

/**
 * Resolves the version/versionType to embed in a preview URL.
 *
 * Order of preference:
 *   1. Explicit version + versionType already in the source URL (user pinned a version).
 *   2. The live published version, if any.
 *   3. Nothing — caller should omit version params from the URL.
 */
export const resolvePreviewVersion = (
  sourceParams: URLSearchParams,
  versionHistory: AutoflowVersionHistorySummary[],
): { version: string; versionType: string } | null => {
  const sourceVersion = sourceParams.get('version');
  const sourceVersionType = sourceParams.get('versionType');
  if (sourceVersion && sourceVersionType) {
    return { version: sourceVersion, versionType: sourceVersionType };
  }

  const published = versionHistory.find(v => v.is_published === true);
  if (published) {
    return {
      version: String(published.version),
      versionType: published.version_type,
    };
  }

  return null;
};

export const shouldShowAgentBuilder = (
  view?: BuilderView,
  isAssistBuildingBlocksEnabled?: boolean,
): boolean => {
  const supportedChannels: BuilderView[] = [
    'widget',
    'api',
    'slack',
    'voice',
    'web_call',
    'email',
  ];
  if (isAssistBuildingBlocksEnabled) {
    supportedChannels.push('playbook');
  }
  return view ? supportedChannels.includes(view) : false;
};

export const replaceIdInRoute = (path: string, intentId: string) =>
  path.replace(':intentId?', intentId);

export const getWorkflowValueFromSearch = (
  search: string,
  value: 'intentId' | 'workflowId',
) => {
  const params = new URLSearchParams(search);
  // Try exact match first, then case-insensitive fallback
  const exact = params.get(value);
  if (exact !== null) {
    return exact;
  }
  const lowerValue = value.toLowerCase();
  for (const [key, val] of params.entries()) {
    if (key.toLowerCase() === lowerValue) {
      return val;
    }
  }
  return '';
};

const MAX_AUTOFLOW_POLICY_WORD_LENGTH = 3000;
const AUTOFLOW_POLICY_VALIDATION_MESSAGE = `Autoflow policy descriptions must stay under ${MAX_AUTOFLOW_POLICY_WORD_LENGTH} words`;

export const validateAutoflowPolicy = (policy = '') => {
  const numWords = wordCount(stripHTML(policy));

  if (numWords >= MAX_AUTOFLOW_POLICY_WORD_LENGTH) {
    return AUTOFLOW_POLICY_VALIDATION_MESSAGE;
  }

  return false;
};

/**
 * Needed because initial values can be `null`, but after editing they can
 * become '' (empty string).
 *
 * So normalize the values to '' (empty string).
 */
const prepareCustomizationDataForComparison = <
  T extends CustomEventCustomizationSettings | undefined,
>(
  customizationData: T,
): T => {
  return (
    customizationData && {
      ...customizationData,
      properties: customizationData.properties.map(property => ({
        ...property,
        default_cv_id: property.default_cv_id ?? '',
        value: property.value ?? '',
      })),
    }
  );
};

export const isCustomEventCustomizationDataPristine = (
  initial: CustomEventCustomizationSettings | undefined,
  current: CustomEventCustomizationSettings | undefined,
): boolean =>
  isEqual(
    prepareCustomizationDataForComparison(initial),
    prepareCustomizationDataForComparison(current),
  );

export const doesChannelSupportWorkflowAsTool = (
  view: BuilderView,
  isAssistBuildingBlocksEnabled?: boolean,
) => {
  const supported = ['widget', 'slack', 'api', 'voice', 'web_call'];
  if (isAssistBuildingBlocksEnabled) {
    supported.push('playbook');
  }
  return supported.includes(view);
};
